// SignIn.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { LinkedIn } from 'react-linkedin-login-oauth2';
import { jwtDecode } from "jwt-decode";
import backendURL, { google_login_api, linkedin_client_id, linkedin_redirect_uri } from "../config";
import './SignIn.css'; // Import your custom CSS for styling

// Make sure you have added the custom CSS to your project as suggested.

export default function UserLogin() {

  useEffect(() => {
    document.title = 'Login | KlonIT Web Application';
  }, []);

  // --- NO CHANGES TO LOGIC OR STATE ---
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${backendURL}/login`, { email, password });
      const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
      if (responseData) {
        localStorage.removeItem('my_access_token');
        localStorage.removeItem('my_user');
        localStorage.setItem('access_token', responseData.access_token);
        localStorage.setItem('user', JSON.stringify(responseData.user));
        localStorage.setItem('customer_portal_token', responseData.customer_portal_token);
        setIsLoading(false);
        const nextStepUrl = responseData.next_step || "/Dashboard";
        if (["/update-profile", "/select-plan"].includes(nextStepUrl)) {
          window.location.href = "/SignUp-Process-Steps"
        } else {
          window.location.href = "/Dashboard"
        }
      } else {
        throw new Error(response.data.message || "Login failed.");
      }
    } catch (error) {
      if (error.response) {
        setError(error.response.data.message);
      } else if (error.request) {
        setError("No response received from API.");
      } else {
        setError("Error setting up request");
      }
    }
  };

  const handleLinkedInLoginSuccess = async (response) => {
    setError(null);
    const authCode = response;
    if (!authCode) {
      setError("LinkedIn authentication code not received.");
      console.error("LinkedIn Error: No auth code received", response);
      return;
    }
    let payloadObject = { auth_code: authCode, redirect_uri: linkedin_redirect_uri }
    try {
      const tokenResponse = await axios.post(`${backendURL}/api/linkedin/oauth`, payloadObject, { headers: { 'Content-Type': 'application/json' } });
      const profileResponseData = typeof tokenResponse.data.data === 'string' ? JSON.parse(tokenResponse.data.data) : tokenResponse.data.data;
      const linkedInUser = profileResponseData?.user;
      const payload = { email: linkedInUser.email, social_id: linkedInUser.sub, provider: 'linkedin' }
      const socialResponse = await axios.post(`${backendURL}/social-login`, payload);
      const socialResponseData = typeof socialResponse.data.data === 'string' ? JSON.parse(socialResponse.data.data) : socialResponse.data.data;
      if (socialResponseData) {
        localStorage.removeItem('my_access_token');
        localStorage.removeItem('my_user');
        localStorage.setItem('access_token', socialResponseData.access_token);
        localStorage.setItem('user', JSON.stringify(socialResponseData.user));
        localStorage.setItem('customer_portal_token', socialResponseData.customer_portal_token);
        setIsLoading(false);
        const nextStepUrl = socialResponseData.next_step || "/Dashboard";
        if (["/update-profile", "/select-plan"].includes(nextStepUrl)) {
          window.location.href = "/SignUp-Process-Steps"
        } else {
          window.location.href = "/Dashboard"
        }
      } else {
        throw new Error(response.data.message || "Login failed.");
      }
    } catch (error) {
      setError(error?.response?.data?.message || "No username or password found. Please check you login or sign-up");
    }
  };

  const handleLinkedInLoginFailure = (error) => {
    if (error?.error != "user_closed_popup") {
      console.error("LinkedIn signup failure:", error);
      setError("LinkedIn signup failed.");
    }
  };

  const handleGoogleLoginSuccess = async (response) => {
    try {
      const { credential } = response;
      const decodedToken = jwtDecode(credential);
      const { sub, email } = decodedToken;
      const payload = { email, social_id: sub, provider: 'google' }
      const socialResponse = await axios.post(`${backendURL}/social-login`, payload);
      const socialResponseData = typeof socialResponse.data.data === 'string' ? JSON.parse(socialResponse.data.data) : socialResponse.data.data;
      if (socialResponseData) {
        localStorage.removeItem('my_access_token');
        localStorage.removeItem('my_user');
        localStorage.setItem('access_token', socialResponseData.access_token);
        localStorage.setItem('user', JSON.stringify(socialResponseData.user));
        localStorage.setItem('customer_portal_token', socialResponseData.customer_portal_token);
        setIsLoading(false);
        const nextStepUrl = socialResponseData.next_step || "/Dashboard";
        if (["/update-profile", "/select-plan"].includes(nextStepUrl)) {
          window.location.href = "/SignUp-Process-Steps"
        } else {
          window.location.href = "/Dashboard"
        }
      } else {
        throw new Error(response.data.message || "Login failed.");
      }
    } catch (error) {
      setError(error?.response?.data?.message || "No username or password found. Please check you login or sign-up");
    }
  };

  const handleGoogleLoginFailure = (error) => {
    setError("No username or password found. Please check you login or sign-up");
  };
  // --- END OF UNCHANGED LOGIC ---


  return (
    <GoogleOAuthProvider clientId={google_login_api}>
      <div className="container-fluid p-0 vh-100">
        <div className="row g-0 h-100">
          {/* Left Branding Panel */}
          <div className="col-lg-6 d-none d-lg-block auth-branding-panel">
            <div className="p-5">
              <h1>KlonIT</h1>
              <p className="lead">Clone your expertise. Scale your reach.</p>
            </div>
          </div>

          {/* Right Form Panel */}
          <div className="col-lg-6 auth-form-panel">
            <div className="auth-form-container">

              <div className="text-center mb-4">
                <h1 className="h2 fw-bold">Welcome Back!</h1>
                <p className="lead">Sign in to continue to your dashboard.</p>
              </div>

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <div className="d-grid gap-3 mb-4">
                <div id="google-login-button">
                  <GoogleLogin
                    onSuccess={handleGoogleLoginSuccess}
                    onError={handleGoogleLoginFailure}
                    useOneTap
                    theme="outline"
                    size="large"
                    shape="rectangular"
                    width="100%"
                  />
                </div>
                <LinkedIn
                  clientId={linkedin_client_id}
                  redirectUri={linkedin_redirect_uri}
                  onSuccess={handleLinkedInLoginSuccess}
                  onError={handleLinkedInLoginFailure}
                  scope="openid profile email"
                >
                  {({ linkedInLogin }) => (
                    <button type="button" onClick={linkedInLogin} className="btn btn-linkedin-custom btn-lg">
                      {/* You can add an SVG icon here for LinkedIn */}
                      Sign in with LinkedIn
                    </button>
                  )}
                </LinkedIn>
              </div>

              <div className="row align-items-center my-4">
                <div className="col"><hr /></div>
                <div className="col-auto text-uppercase divider-text">Or</div>
                <div className="col"><hr /></div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input
                    className="form-control form-control-lg"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Password</label>
                  <input
                    className="form-control form-control-lg"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                  />
                  <div className="text-end mt-1">
                    <small>
                      <a href="/forgot-password">Forgot password?</a>
                    </small>
                  </div>
                </div>

                <div className="d-grid gap-2 mt-4">
                  <button className="btn btn-primary btn-lg" type="submit" disabled={isLoading}>
                    {isLoading ? "Signing In..." : "Sign In"}
                  </button>
                </div>
              </form>

              <div className="text-center mt-4">
                Don't have an account? <a href="/SignUp">Sign up</a>
              </div>

            </div>
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
}